import React, { useState, useEffect } from "react";
import { Settings, Wrench, LineChart, Flag, Bike, Plus, Pencil, Trash2, X, Save } from "lucide-react";
import { api } from "../lib/api";

const ICONS = { Settings, Wrench, LineChart, Flag, Bike };
const ICON_KEYS = Object.keys(ICONS);

const emptyService = { title_pt: "", title_en: "", desc_pt: "", desc_en: "", icon: "Settings", order: 0 };

const ServicesManager = () => {
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null); // service object or "new"
  const [form, setForm] = useState(emptyService);
  const [saving, setSaving] = useState(false);

  const load = () => api.listServices().then(setServices).catch(() => {});

  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ ...emptyService, order: services.length + 1 }); setEditing("new"); };
  const openEdit = (s) => { setForm({ ...s }); setEditing(s.id); };
  const close = () => { setEditing(null); setForm(emptyService); };

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing === "new") await api.createService(form);
      else await api.updateService(editing, form);
      await load();
      close();
    } catch (err) {
      alert("Erro ao guardar o serviço.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminar este serviço?")) return;
    setServices((prev) => prev.filter((s) => s.id !== id));
    try { await api.deleteService(id); } catch { load(); }
  };

  const inputCls = "bf-input w-full rounded-xl px-4 py-2.5 text-sm placeholder:text-gray-500";
  const labelCls = "block text-xs font-medium text-gray-400 mb-1.5";

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-gray-400 text-sm">{services.length} serviço(s) — visíveis na página "Serviços"</p>
        <button onClick={openNew} className="bf-btn-primary text-white font-semibold px-5 py-2.5 rounded-xl inline-flex items-center gap-2 text-sm">
          <Plus className="w-4 h-4" /> Novo Serviço
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {services.map((s) => {
          const Icon = ICONS[s.icon] || Settings;
          return (
            <div key={s.id} className="bf-glass rounded-2xl p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#22d3ee]/20 to-[#22d3ee]/5 border border-[#22d3ee]/30 flex items-center justify-center">
                  <Icon className="w-6 h-6 text-[#22d3ee]" />
                </div>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(s)} className="p-2 rounded-lg bg-white/5 text-gray-300 hover:text-[#67e8f9] border border-white/10 transition-colors">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => remove(s.id)} className="p-2 rounded-lg bg-red-500/10 text-red-400 border border-red-500/20 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <h3 className="text-white font-bold mb-1">{s.title_pt}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{s.desc_pt}</p>
            </div>
          );
        })}
      </div>

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4" onClick={close}>
          <div className="bf-glass rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-white font-bold text-lg">{editing === "new" ? "Novo Serviço" : "Editar Serviço"}</h3>
              <button onClick={close} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={save} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Título (PT) *</label>
                  <input name="title_pt" required value={form.title_pt} onChange={onChange} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Título (EN)</label>
                  <input name="title_en" value={form.title_en} onChange={onChange} className={inputCls} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Descrição (PT) *</label>
                <textarea name="desc_pt" required rows={2} value={form.desc_pt} onChange={onChange} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Descrição (EN)</label>
                <textarea name="desc_en" rows={2} value={form.desc_en} onChange={onChange} className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Ícone</label>
                  <select name="icon" value={form.icon} onChange={onChange} className={inputCls}>
                    {ICON_KEYS.map((k) => <option key={k} value={k} className="bg-[#131b32]">{k}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Ordem</label>
                  <input type="number" name="order" value={form.order} onChange={onChange} className={inputCls} />
                </div>
              </div>
              <button type="submit" disabled={saving} className="bf-btn-primary text-white font-semibold px-6 py-3 rounded-xl w-full inline-flex items-center justify-center gap-2 text-sm disabled:opacity-60">
                <Save className="w-4 h-4" /> {saving ? "A guardar..." : "Guardar"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesManager;
